/**
 * Filter bar column layout
 * How many fields share a row of the InlineFilterBar.
 */

/** Four columns is the narrowest a field may get: a quarter of the bar, gap aside. */
const MAX_COLUMNS = 4;
const MIN_COLUMNS = 3;
const MAX_ROWS = 3;

const EMPTY_CELL_PENALTY = 10;
const EXTRA_ROW_PENALTY = 100;
const LONELY_FIELD_PENALTY = 50;

/**
 * Picks how many fields go on each row so the last row comes out as full as
 * possible — four columns for eight fields, three for nine — instead of leaving
 * a single field next to a stretch of empty bar. Ties go to fewer columns,
 * which makes each field wider.
 */
export const pickColumns = (fieldCount: number): number => {
  if (fieldCount <= MIN_COLUMNS) return Math.max(fieldCount, 1);

  let best = MAX_COLUMNS;
  let bestPenalty = Infinity;

  for (let columns = MIN_COLUMNS; columns <= MAX_COLUMNS; columns++) {
    const rows = Math.ceil(fieldCount / columns);
    const emptyCells = rows * columns - fieldCount;
    const lastRowCount = fieldCount - (rows - 1) * columns;

    const penalty =
      emptyCells * EMPTY_CELL_PENALTY +
      (rows > MAX_ROWS ? EXTRA_ROW_PENALTY : 0) +
      (rows > 1 && lastRowCount === 1 ? LONELY_FIELD_PENALTY : 0) +
      columns;

    if (penalty < bestPenalty) {
      bestPenalty = penalty;
      best = columns;
    }
  }

  return best;
};
